import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { productionPlanningService, type MaterialAuditRow } from '@/services/production-planning.service';

const { Title, Text } = Typography;

const MaterialAuditPage: React.FC = () => {
  const query = useQuery({
    queryKey: ['production-planning', 'material-audit'],
    queryFn: () => productionPlanningService.materialAudit(),
  });
  const rows = query.data ?? [];
  const shortages = rows.filter((row) => row.shortage_qty > 0).length;

  const columns: ColumnsType<MaterialAuditRow> = [
    {
      title: 'Promise',
      dataIndex: 'promise_date',
      width: 120,
      render: (value: string | null) => (value ? dayjs(value).format('DD-MMM-YYYY') : '—'),
      sorter: (a, b) => (a.promise_date || '').localeCompare(b.promise_date || ''),
    },
    {
      title: 'Source',
      key: 'source',
      width: 180,
      render: (_, row) =>
        row.source === 'work_order' ? (
          <Link to={`/mfg/work-orders/${row.source_id}`}>{row.source_number}</Link>
        ) : (
          <span>{row.source_number}</span>
        ),
    },
    { title: 'Finished item', dataIndex: 'finished_item', ellipsis: true },
    {
      title: 'Component',
      key: 'component',
      render: (_, row) =>
        row.component_code ? `${row.component_code}${row.component_name ? ` — ${row.component_name}` : ''}` : '—',
    },
    {
      title: 'Need',
      key: 'need',
      width: 120,
      align: 'right',
      render: (_, row) => (row.required_qty ? `${row.required_qty} ${row.uom || ''}` : '—'),
    },
    { title: 'Free stock', dataIndex: 'free_stock', width: 110, align: 'right' },
    { title: 'Open PO', dataIndex: 'open_po_qty', width: 100, align: 'right' },
    {
      title: 'Covered by stock',
      dataIndex: 'allocated_stock',
      width: 140,
      align: 'right',
    },
    { title: 'Covered by PO', dataIndex: 'allocated_po', width: 130, align: 'right' },
    {
      title: 'Shortage',
      dataIndex: 'shortage_qty',
      width: 110,
      align: 'right',
      render: (value: number) => (value > 0 ? <Tag color="red">{value}</Tag> : <Tag color="green">0</Tag>),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      ellipsis: true,
      render: (value: string | null) => value || '',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        Material audit
      </Title>
      <Text type="secondary">
        One BOM level for open work orders and production-request quantity that is not on a work order yet. Scrap is included. Free stock and open purchase orders are used in promise-date order, so a later job sees what earlier jobs already need.
      </Text>
      {shortages > 0 ? (
        <Alert style={{ margin: '16px 0' }} type="warning" showIcon message={`${shortages} component lines are short.`} />
      ) : null}
      <Card style={{ marginTop: 16 }}>
        <Table
          rowKey={(row) => `${row.source}-${row.source_id}-${row.component_item_id || 'none'}`}
          loading={query.isLoading}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  );
};

export default MaterialAuditPage;
